// The top ten, dragged into order, with at most three of them locked.
//
// Rank is a weight and a lock is a constraint, and the difference matters: the
// weighted ones bend, the locked ones are what the day is built around. The cap
// exists because unlimited hard constraints make the problem infeasible and turn
// the solver into a source of arguments rather than answers.

import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApp } from '../../data/store.tsx';
import { fmtTime } from '../../engine/time.ts';
import '../../styles/planning-refinements.css';

function Row({ id, index, order }: { id: string; index: number; order: string[] }) {
  const { state, actions, derived } = useApp();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const artist = derived.artistById.get(id);
  const entry = derived.myMustSee.find((m) => m.artistId === id);
  const sets = state.sets.filter((s) => s.artistId === id);
  const cap = state.workspace?.settings.hardLockCap ?? state.config.hardLockCap;
  const lockedCount = derived.myMustSee.filter((m) => m.hardLock).length;
  const canLock = entry?.hardLock || lockedCount < cap;

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[isDragging ? 'dragging' : '', entry?.hardLock ? 'locked' : ''].filter(Boolean).join(' ')}
    >
      <button type="button" className="small ghost planning-drag-handle" {...attributes} {...listeners}
        aria-label={`Drag to reorder ${artist?.name ?? id}`}>
        <span className="rank">{index + 1}</span>
      </button>
      <span className="planning-rank-artist">
        {artist?.name ?? id}
        <span className="sub" style={{ display: 'block' }}>
          {sets
            .map((s) => `${derived.stageById.get(s.stageId)?.name} ${fmtTime(s.startTs)}`)
            .join(', ') || 'Set time unavailable'}
        </span>
      </span>
      <span className="muted planning-lock-state" style={{ fontSize: 14 }}>
        {entry?.hardLock ? 'Locked' : 'Ranked'}
      </span>
      <div className="planning-rank-actions">
      <button type="button" className="small ghost" disabled={index === 0}
        aria-label={`Move ${artist?.name ?? id} up`}
        onClick={() => void actions.setTopTen(arrayMove(order, index, index - 1))}>Up</button>
      <button type="button" className="small ghost" disabled={index === order.length - 1}
        aria-label={`Move ${artist?.name ?? id} down`}
        onClick={() => void actions.setTopTen(arrayMove(order, index, index + 1))}>Down</button>
      <button
        type="button"
        className="small ghost"
        disabled={!canLock}
        aria-label={`${entry?.hardLock ? 'Unlock' : 'Lock'} ${artist?.name ?? id}`}
        title={!canLock ? `All ${cap} locks are in use` : undefined}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          void actions.toggleHardLock(id);
        }}
      >
        {entry?.hardLock ? 'Unlock' : 'Lock'}
      </button>
      <button type="button" className="small ghost" aria-label={`Remove ${artist?.name ?? id} from Top ten`}
        onClick={() => void actions.setTopTen(order.filter((artistId) => artistId !== id))}>Remove</button>
      </div>
    </li>
  );
}

export function Priorities() {
  const { state, actions, derived } = useApp();
  const [adding, setAdding] = useState('');
  const order = useMemo(() => derived.myMustSee.map((m) => m.artistId), [derived.myMustSee]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    void actions.setTopTen(arrayMove(order, from, to));
  };

  const candidates = state.artists
    .filter((a) => !order.includes(a.id))
    .filter((a) => a.name.toLowerCase().includes(adding.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 8);

  const lockedCount = derived.myMustSee.filter((m) => m.hardLock).length;
  const cap = state.workspace?.settings.hardLockCap ?? state.config.hardLockCap;

  return (
    <div className="cols planning-page priorities-page">
      <div>
        <h1>Top ten</h1>
        <p className="lede">
          Add up to ten artists, then use Up and Down or drag a rank number to order them.
          Lock an artist to require their sets in suggested schedules.
        </p>
        {order.length === 0 ? (
          <p className="muted">Search for an artist in Add to Top ten to start your list.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              <ul className="sortable">
                {order.map((id, i) => (
                  <Row key={id} id={id} index={i} order={order} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div>
        <div className="panel">
          <h3>Locks</h3>
          <p style={{ margin: 0, fontSize: 16 }}>
            {lockedCount} of {cap} used.
          </p>
          <p className="muted" style={{ fontSize: 16, marginBottom: 0 }}>
            Suggested schedules include locked sets and walking time. Overlapping locks may need a choice.
          </p>
        </div>

        <div className="panel">
          <h3>Add to Top ten</h3>
          <p className="muted">{order.length} of 10 artists added.</p>
          <input
            type="search"
            aria-label="Search artists to add to Top ten"
            placeholder="Artist name"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            style={{ width: '100%' }}
          />
          <div className="rowlist" style={{ marginTop: 8 }}>
            {candidates.map((a) => (
              <div key={a.id} className="row">
                <span>{a.name}</span>
                <button
                  className="small"
                  aria-label={`Add ${a.name} to Top ten`}
                  disabled={order.length >= 10}
                  onClick={() => void actions.setTopTen([...order, a.id])}
                >
                  Add
                </button>
              </div>
            ))}
          </div>
          {candidates.length === 0 && <p className="muted">No matching artists to add.</p>}
          {order.length >= 10 && <p className="muted">Your Top ten is full. Remove an artist to add another.</p>}
        </div>
      </div>
    </div>
  );
}
